// packages/shared/src/constants/backtester.constants.ts
// Single source of truth for backtester domain value-sets (ADR-20, section 5).

export const BACKTEST_ALL_SYMBOLS = "ALL" as const;
export const BACKTEST_ALL_POSITIONS = "ALL" as const;
export const BACKTESTER_ANALYSIS_DEFAULT_ITERATIONS = 1000 as const;

export const STRATEGY_EDITOR_MODE = ["create", "edit", "clone"] as const;
export type TStrategyEditorMode = (typeof STRATEGY_EDITOR_MODE)[number];
export const STRATEGY_EDITOR_MODE_OBJ = {
  CREATE: "create",
  EDIT: "edit",
  CLONE: "clone",
} as const satisfies Record<string, TStrategyEditorMode>;

// ── tradeDirection ──────────────────────────────────────────────────────────
export const TRADE_DIRECTION = ["long", "short"] as const;
export type TTradeDirection = (typeof TRADE_DIRECTION)[number];
export const TRADE_DIRECTION_OBJ = {
  LONG: "long",
  SHORT: "short",
} as const satisfies Record<string, TTradeDirection>;

export const SLOPE_DIRECTION = ["up", "down", "flat"] as const;
export type TSlopeDirection = (typeof SLOPE_DIRECTION)[number];
export const SLOPE_DIRECTION_OBJ = {
  UP: SLOPE_DIRECTION[0],
  DOWN: SLOPE_DIRECTION[1],
  FLAT: SLOPE_DIRECTION[2],
} as const satisfies Record<string, TSlopeDirection>;

// ── commissionType ──────────────────────────────────────────────────────────
export const COMMISSION_TYPE = ["percent", "perContract", "perOrder"] as const;
export type TCommissionType = (typeof COMMISSION_TYPE)[number];
export const COMMISSION_TYPE_OBJ = {
  PERCENT: "percent",
  PER_CONTRACT: "perContract",
  PER_ORDER: "perOrder",
} as const satisfies Record<string, TCommissionType>;

// ── slippageType ────────────────────────────────────────────────────────────
export const SLIPPAGE_TYPE = ["ticks", "percent"] as const;
export type TSlippageType = (typeof SLIPPAGE_TYPE)[number];
export const SLIPPAGE_TYPE_OBJ = {
  TICKS: "ticks",
  PERCENT: "percent",
} as const satisfies Record<string, TSlippageType>;

// ── sizingMode ──────────────────────────────────────────────────────────────
// Canonical set (runtime). kelly/percent removed — were not implemented.
export const SIZING_MODE = ["fixed", "percentOfEquity", "cash"] as const;
export type TSizingMode = (typeof SIZING_MODE)[number];
export const SIZING_MODE_OBJ = {
  FIXED: "fixed",
  PERCENT_OF_EQUITY: "percentOfEquity",
  CASH: "cash",
} as const satisfies Record<string, TSizingMode>;

// ── strategySource ──────────────────────────────────────────────────────────
export const STRATEGY_SOURCE = ["builtin", "user"] as const;
export type TStrategySource = (typeof STRATEGY_SOURCE)[number];
export const STRATEGY_SOURCE_OBJ = {
  BUILTIN: "builtin",
  USER: "user",
} as const satisfies Record<string, TStrategySource>;

// ── aiDecision ──────────────────────────────────────────────────────────────
// AI gate decision (lowercase — AI output format for backtester).
export const AI_DECISION = ["yes", "no", "hold"] as const;
export type TAiDecision = (typeof AI_DECISION)[number];
export const AI_DECISION_OBJ = {
  YES: "yes",
  NO: "no",
  HOLD: "hold",
} as const satisfies Record<string, TAiDecision>;

// ── aiGateTrigger ───────────────────────────────────────────────────────────
// When the AI gate fires (backtester only, but used across schema + type + strategies).
export const AI_GATE_TRIGGER = ["onEntry", "onExit", "onSignal", "custom"] as const;
export type TAiGateTrigger = (typeof AI_GATE_TRIGGER)[number];
export const AI_GATE_TRIGGER_OBJ = {
  ON_ENTRY: "onEntry",
  ON_EXIT: "onExit",
  ON_SIGNAL: "onSignal",
  CUSTOM: "custom",
} as const satisfies Record<string, TAiGateTrigger>;

// ── aiGateMode ──────────────────────────────────────────────────────────────
// How the AI gate acts (filter = hard block, advisor = soft suggestion).
export const AI_GATE_MODE = ["filter", "advisor"] as const;
export type TAiGateMode = (typeof AI_GATE_MODE)[number];
export const AI_GATE_MODE_OBJ = {
  FILTER: "filter",
  ADVISOR: "advisor",
} as const satisfies Record<string, TAiGateMode>;
